import React, { useState, useEffect, useRef, ChangeEvent } from 'react';
import { useAuth } from '../../../AuthContext';
import { db } from '../../../firebaseConfig';
import {
    doc,
    getDoc,
    collection,
    addDoc,
    updateDoc,
    Timestamp,
    where,
    query,
    getDocs
} from 'firebase/firestore';
import GradientSubmitButton from '../../GradientSubmitButton';
import { LeadData, addNewLead, updateLead } from '../../../pages/Clients/Admin/Function/LeadFunctions';
import getCurrentScreen from '../../../Helper/GetScreen';

// Types
interface User {
    id: string;
    name: string;
    lastName: string;
    email: string;
    distribution?: number;
}

interface Lead {
    id: string;
    name: string;
    number: string;
    email?: string;
    assignedTo: string;
    [key: string]: any;
}

interface FollowUp {
    id: string;
    leadId: string;
    followUp: string;
    followUpTime: Timestamp;
    userId: string;
    assignedUserName?: string;
    isDone: boolean;
}

interface AddUpdateLeadModalProps {
    isOpen: boolean;
    onClose: () => void;
    editingId: string | null;
    leadData?: Partial<LeadData>;
    assignedToOptions: User[];
    sources: { id: string; source: string }[];
    subSources: { id: string; subSource: string; sourceId: string }[];
    campaigns: { id: string; campaign: string }[];
    pipelines: { id: string; pipelineName: any }[];
    currentUserPipelines: string[];
    isAdmin: boolean;
    isManager: boolean;
    isStaff: boolean;
    onSuccess?: (message: string) => void;
    onLeadAdded?: (lead: LeadData) => void;
    onLeadUpdated?: (lead: LeadData) => void;
}

const AddUpdateLeadModal: React.FC<AddUpdateLeadModalProps> = ({
    isOpen,
    onClose,
    editingId,
    leadData = {},
    assignedToOptions,
    sources,
    subSources,
    campaigns,
    pipelines,
    currentUserPipelines,
    isAdmin,
    isManager,
    isStaff,
    onSuccess,
    onLeadAdded,
    onLeadUpdated
}) => {
    const { currentUser, userData } = useAuth();
    const [newLead, setNewLead] = useState<Partial<LeadData>>(leadData);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [phoneValidationMessage, setPhoneValidationMessage] = useState<string>("");
    const [isPhoneValidating, setIsPhoneValidating] = useState<boolean>(false);
    const [phoneValidationType, setPhoneValidationType] = useState<'error' | 'warning' | 'success' | ''>("");
    const inputRef = useRef<HTMLInputElement>(null);

    // Update validation state variables
    const [updateValidationMessage, setUpdateValidationMessage] = useState<string>("");
    const [updateValidationType, setUpdateValidationType] = useState<'error' | 'warning' | 'success' | ''>("");

    // Alternate phone states
    const [isAlternatePhoneRequired, setIsAlternatePhoneRequired] = useState<boolean>(false);
    const [showSecondAlternatePhone, setShowSecondAlternatePhone] = useState<boolean>(false);

    const currentScreen = getCurrentScreen('', false, false, isStaff || false);

    // Helper function to convert Timestamp to Date string
    const convertToDateString = (timestamp: Timestamp | Date): string => {
        if (timestamp instanceof Timestamp) {
            const date = timestamp.toDate();
            date.setMinutes(date.getMinutes() + 330); // Add 5 hours and 30 minutes (330 minutes)
            return date.toISOString().slice(0, 16); // Returns in 'YYYY-MM-DDTHH:mm' format
        } else {
            throw new Error("Invalid timestamp type");
        }
    };

    // Email validation function
    const isValidEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // Check if number already exists in the database
    const checkNumberExists = async (number: string, excludeLeadId?: string): Promise<{
        exists: boolean;
        leadData: Lead | null;
        assigneeName?: string;
    }> => {
        if (!userData?.companyId || !number) return { exists: false, leadData: null };

        try {
            const leadsRef = collection(db, `companies/${userData.companyId}/leads`);
            let q = query(leadsRef, where("number", "==", number));

            const querySnapshot = await getDocs(q);

            // If we're editing a lead, exclude the current lead from duplicate check
            if (excludeLeadId) {
                const duplicateDocs = querySnapshot.docs.filter(doc => doc.id !== excludeLeadId);
                if (duplicateDocs.length > 0) {
                    const docData = duplicateDocs[0].data();
                    const leadData = { id: duplicateDocs[0].id, ...docData } as Lead;

                    // Fetch assignee name
                    let assigneeName = "Unknown";
                    if (leadData.assignedTo) {
                        try {
                            const userDoc = await getDoc(doc(db, 'users', leadData.assignedTo));
                            if (userDoc.exists()) {
                                assigneeName = userDoc.data().name || "Unknown";
                            }
                        } catch (error) {
                            console.error("Error fetching assignee name:", error);
                        }
                    }

                    return {
                        exists: true,
                        leadData: leadData,
                        assigneeName: assigneeName
                    };
                }
                return { exists: false, leadData: null };
            }

            if (!querySnapshot.empty) {
                const firstDoc = querySnapshot.docs[0];
                const docData = firstDoc.data();
                const leadData = { id: firstDoc.id, ...docData } as Lead;

                // Fetch assignee name
                let assigneeName = "Unknown";
                if (leadData.assignedTo) {
                    try {
                        const userDoc = await getDoc(doc(db, 'users', leadData.assignedTo));
                        if (userDoc.exists()) {
                            assigneeName = userDoc.data().name || "Unknown";
                        }
                    } catch (error) {
                        console.error("Error fetching assignee name:", error);
                    }
                }

                return {
                    exists: true,
                    leadData: leadData,
                    assigneeName: assigneeName
                };
            }

            return { exists: false, leadData: null };
        } catch (error) {
            console.error("Error checking number existence:", error);
            return { exists: false, leadData: null };
        }
    };

    // Real-time phone number validation function
    const validatePhoneRealTime = async (phoneNumber: string) => {
        setPhoneValidationMessage("");
        setPhoneValidationType("");

        if (!phoneNumber || phoneNumber.length < 1) {
            return;
        }

        setIsPhoneValidating(true);

        try {
            let formattedNumber = phoneNumber.replace(/\D/g, '');
            
            // Add + prefix if not already present and number has digits
            if (!phoneNumber.startsWith('+') && formattedNumber.length > 0) {
                formattedNumber = "+" + formattedNumber;
            } else if (phoneNumber.startsWith('+')) {
                formattedNumber = phoneNumber;
            } else {
                formattedNumber = "+" + formattedNumber;
            }

            const result = await checkNumberExists(formattedNumber, editingId || undefined);

            if (result.exists && result.leadData) {
                const existingLeadName = result.leadData.name || "Unknown";
                const assigneeName = result.assigneeName || "Unknown";
                setPhoneValidationMessage(`Lead already exists: ${existingLeadName} (Assigned to: ${assigneeName})`);
                setPhoneValidationType("error");
            } else {
                setPhoneValidationMessage("Phone number is available");
                setPhoneValidationType("success");
            }
        } catch (error) {
            console.error("Error validating phone number:", error);
            setPhoneValidationMessage("Error validating phone number");
            setPhoneValidationType("error");
        } finally {
            setIsPhoneValidating(false);
        }
    };

    // Validation function for required fields
    const validateRequiredFields = () => {
        if (!newLead.name || newLead.name.trim() === '') {
            return 'Name is required';
        }

        if (!newLead.number || newLead.number.trim() === '') {
            return 'Phone number is required';
        }

        if (!newLead.assignedTo || newLead.assignedTo.trim() === '') {
            return 'Assigned To is required';
        }

        if (!newLead.sourceId || newLead.sourceId.trim() === '') {
            return 'Source is required';
        }

        return null;
    };

    // Handle name change with uppercase conversion
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const input = e.target;
        const start = input.selectionStart ?? 0;
        const end = input.selectionEnd ?? 0;
        const upperCased = input.value.toLocaleUpperCase();

        setNewLead((prev) => ({ ...prev, name: upperCased }));

        // Restore cursor after render
        requestAnimationFrame(() => {
            if (input) {
                input.setSelectionRange(start, end);
            }
        });
    };


    const handlePhoneBlur = (field: "number" | "alternateNumber1" | "alternateNumber2") => {
        const fieldValue = newLead[field];
        // Only set +91 for the main phone number field (required field)
        if (field === "number" && (!fieldValue || fieldValue.trim() === "")) {
            setNewLead({ ...newLead, [field]: "+91" });
        }
        // For alternate numbers, clear the field if it's empty or only contains +91
        if ((field === "alternateNumber1" || field === "alternateNumber2") && 
            (!fieldValue || fieldValue.trim() === "" || fieldValue.trim() === "+91")) {
            setNewLead({ ...newLead, [field]: "" });
        }
    };
    const handlePhoneFocus = (field: "number" | "alternateNumber1" | "alternateNumber2") => {
        if (field === "number") {
            // Clear validation state when focusing on main phone number
            setPhoneValidationMessage('');
            setPhoneValidationType('');
        }

        const fieldValue = newLead[field];
        if (!fieldValue || fieldValue.trim() === "") {
            setNewLead({ ...newLead, [field]: "+91" });
        }
    };

    // Helper function to filter out non-numeric characters (except + for country code)
    const handlePhoneChange = (field: "number" | "alternateNumber1" | "alternateNumber2", value: string) => {
        // Allow only numbers and + symbol (for country code), remove letters and spaces
        const numericValue = value.replace(/[^0-9+]/g, '');
        setNewLead({ ...newLead, [field]: numericValue });
        
        // Clear update validation message when phone number changes
        if (field === "number") {
            setUpdateValidationMessage("");
            setUpdateValidationType("");
        }
    };

    // Handle pipeline selection
    const handlePipelineSelection = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedPipeline = e.target.value;
        setNewLead(prev => {
            const currentPipelines = prev.pipelines || [];
            if (selectedPipeline && !currentPipelines.includes(selectedPipeline)) {
                return {
                    ...prev,
                    pipelines: [...currentPipelines, selectedPipeline]
                };
            }
            return prev;
        });
    };

    const handleRemovePipeline = (pipelineId: string) => {
        setNewLead(prev => ({
            ...prev,
            pipelines: (prev.pipelines ?? []).filter(id => id !== pipelineId)
        }));
    };

    // Handle Add Lead
    const handleAddLead = async () => {
        setSubmitLoading(true);

        // Validate required fields
        const validationError = validateRequiredFields();
        if (validationError) {
            setSubmitLoading(false);
            if (onSuccess) onSuccess(validationError);
            return;
        }

        // Add email validation if needed
        if (newLead.email && !isValidEmail(newLead.email)) {
            setSubmitLoading(false);
            if (onSuccess) onSuccess("Please enter a valid email address");
            return;
        }

        if (!newLead.number || newLead.number.replace(/\D/g, '').length < 1) {
            if (onSuccess) onSuccess("Error: Phone number is required");
            setSubmitLoading(false);
            return;
        } else {
            // Format the number
            let cleanedNumber = newLead.number.replace(/\D/g, '');
            if (!newLead.number.startsWith('+') && cleanedNumber.length > 0) {
                newLead.number = "+" + cleanedNumber;
            } else if (!newLead.number.startsWith('+')) {
                newLead.number = "+" + cleanedNumber;
            }
        }

        // Check if the number already exists
        const numberCheckResult = await checkNumberExists(newLead.number);
        if (numberCheckResult.exists && numberCheckResult.leadData) {
            const existingLeadName = numberCheckResult.leadData.name || "Unknown";
            const assigneeName = numberCheckResult.assigneeName || "Unknown";
            if (onSuccess) onSuccess(`Lead Already Exists with this Number. Existing Lead: ${existingLeadName} (Assigned to: ${assigneeName})`);
            setSubmitLoading(false);
            return;
        }

        const assignedTo = newLead?.assignedTo || "";
        const manualEntrySource = sources.find(s => s.source === "Manual Entry")?.id || "";

        try {
            // Clean alternate numbers - only save if they contain actual numbers beyond +91
            const cleanAlternateNumber1 = newLead.alternateNumber1?.trim();
            const cleanAlternateNumber2 = newLead.alternateNumber2?.trim();
            
            const alternateNumber1 = (cleanAlternateNumber1 && cleanAlternateNumber1 !== "+91" && cleanAlternateNumber1.length > 3) 
                ? cleanAlternateNumber1 : "";
            const alternateNumber2 = (cleanAlternateNumber2 && cleanAlternateNumber2 !== "+91" && cleanAlternateNumber2.length > 3) 
                ? cleanAlternateNumber2 : "";

            const addedLead = await addNewLead(db, currentUser, {
                name: (newLead.name ?? "").trim(),
                email: (newLead.email || "").trim(),
                number: newLead.number,
                alternateNumber1: alternateNumber1,
                alternateNumber2: alternateNumber2,
                reversedNumber: newLead.number.split('').reverse().join(''),
                reversedAlternateNumber1: alternateNumber1 ? alternateNumber1.split('').reverse().join('') : "",
                reversedAlternateNumber2: alternateNumber2 ? alternateNumber2.split('').reverse().join('') : "",
                assignedTo: assignedTo,
                adminNotes: (newLead.adminNotes || ""),
                sourceId: newLead.sourceId || manualEntrySource,
                subSourceId: subSources.find(s => s.id === newLead.subSourceId && s.sourceId === newLead.sourceId)?.id || null,
                stageId: newLead.stageId || "",
                stateId: newLead.stateId || "",
                statusId: newLead.statusId || "",
                campaignId: newLead.campaignId || "",
                admissionStatusId: newLead.admissionStatusId || "",
                isActive: true,
                hasActiveFollowUp: false,
                activeFollowUpId: null,
                createdBy: currentUser ? currentUser.uid : "",
                updatedAt: Timestamp.fromDate(new Date()),
                createdAt: Timestamp.fromDate(new Date()),
                destinationId: newLead.destinationId || "",
                pipelines: newLead.pipelines || [],
                intakeMonth: newLead.intakeMonth || "",
                intakeYear: newLead.intakeYear || 0,
                welcomeCallByAdmissionTeamStatusId: newLead.welcomeCallByAdmissionTeamStatusId || "",
                visaFileProcessingStatusId: newLead.visaFileProcessingStatusId || "",
                applicationMovedStatusId: newLead.applicationMovedStatusId || "",
                conditionalOfferLetterStatusId: newLead.conditionalOfferLetterStatusId || "",
                unconditionalOfferLetterStatusId: newLead.unconditionalOfferLetterStatusId || "",
                feePaymentStatusId: newLead.feePaymentStatusId || "",
                fileSubmissionStatusId: newLead.fileSubmissionStatusId || "",
                preferredStreamId: newLead.preferredStreamId || "",
                subAgentId: newLead.subAgentId || "",
                subAdmissionStatusId: newLead.subAdmissionStatusId || "",
                subStatusId: newLead.subStatusId || "",
            });

            if (newLead?.followUpText) {
                const assignedUserName = userData?.name || 'Unknown';
                
                const followUpData = {
                    followUp: newLead.followUpText,
                    followUpTime: newLead.followUpTime ? newLead.followUpTime : Timestamp.fromDate(new Date()),
                    leadId: addedLead.id,
                    userId: assignedTo || "", // Must use current user's UID for permission validation
                    assignedUserName: assignedUserName,
                    isDone: false,
                    isEdited: false,
                    createdAt: Timestamp.fromDate(new Date()),
                    createdBy: currentUser?.uid || "",
                    originalAssigneeId: assignedTo, // Store original assignee separately
                };

                const followUpRef = await addDoc(collection(db, `companies/${userData?.companyId}/followUps`), followUpData);

                try {
                    const leadRef = doc(db, `companies/${userData?.companyId}/leads`, addedLead.id);
                    const leadSnap = await getDoc(leadRef);
                    if (!leadSnap.exists()) {
                        console.error("Lead does not exist!");
                        return;
                    }

                    await new Promise(resolve => setTimeout(resolve, 3000));

                    await updateDoc(leadRef, {
                        hasActiveFollowUp: true,
                        activeFollowUpId: followUpRef.id,
                        activeFollowUpTime: newLead.followUpTime ? newLead.followUpTime : Timestamp.fromDate(new Date()),
                        activeFollowUpText: newLead.followUpText || null
                    });

                    await updateDoc(followUpRef, {
                        id: followUpRef.id,
                    });
                } catch (error) {
                    console.error("Error updating lead:", error);
                }
            }

            if (onLeadAdded) onLeadAdded(addedLead);
            if (onSuccess) onSuccess("Lead added successfully!");
            onClose();
            setNewLead({});
            window.location.reload();
        } catch (error) {
            console.error("Error adding lead:", error);
            if (onSuccess) onSuccess(`Error adding lead: ${(error as Error).message}`);
        } finally {
            setSubmitLoading(false);
        }
    };

    // Handle Update Lead
    const handleUpdateLead = async () => {
        setSubmitLoading(true);

        if (!editingId) {
            setSubmitLoading(false);
            if (onSuccess) onSuccess("Error: No lead selected for editing");
            return;
        }

        // Validate required fields
        const validationError = validateRequiredFields();
        if (validationError) {
            setSubmitLoading(false);
            if (onSuccess) onSuccess(validationError);
            return;
        }

        if (newLead.number && newLead.number.replace(/\D/g, '').length < 1) {
            if (onSuccess) onSuccess("Error: Phone number is required");
            setSubmitLoading(false);
            return;
        } else if (newLead.number) {
            let cleanedNumber = newLead.number.replace(/\D/g, '');
            if (!newLead.number.startsWith('+') && cleanedNumber.length > 0) {
                newLead.number = "+" + cleanedNumber;
            } else if (!newLead.number.startsWith('+')) {
                newLead.number = "+" + cleanedNumber;
            }
        }

        // Check if the number already exists (excluding the current lead being edited)
        if (newLead.number) {
            // Clear any previous update validation messages
            setUpdateValidationMessage("");
            setUpdateValidationType("");
            
            const numberCheckResult = await checkNumberExists(newLead.number, editingId);
            if (numberCheckResult.exists && numberCheckResult.leadData) {
                const existingLeadName = numberCheckResult.leadData.name || "Unknown";
                const assigneeName = numberCheckResult.assigneeName || "Unknown";
                
                setSubmitLoading(false);
                setUpdateValidationMessage(`Lead Already Exists with this Number. Existing Lead: ${existingLeadName} (Assigned to: ${assigneeName})`);
                setUpdateValidationType("error");
                return;
            }
        }

        // Initialize follow-up related variables
        let hasActiveFollowUp: boolean = false;
        let activeFollowUpId: string | null = null;
        let activeFollowUpTime: Timestamp | null = null;
        let activeFollowUpText: string | null = null;

        // Handle follow-up creation/update
        if (newLead?.followUpText && newLead?.followUpText.trim() !== "") {
            let followUpRef;
            if (newLead.activeFollowUpId) {
                // Mark existing follow-up as done and edited
                const existingFollowUpRef = doc(db, `companies/${userData?.companyId}/followUps`, newLead.activeFollowUpId);
                await updateDoc(existingFollowUpRef, {
                    isDone: true,
                    isEdited: true,
                    updatedAt: Timestamp.fromDate(new Date()),
                    updatedBy: currentUser?.uid || ""
                });

                // Create new follow-up
                const assignedUserName = userData?.name || 'Unknown';
                
                const newFollowUpData = {
                    followUp: newLead.followUpText,
                    followUpTime: newLead.followUpTime ? newLead.followUpTime : Timestamp.fromDate(new Date()),
                    leadId: editingId,
                    userId: newLead.assignedTo || "", // Must use current user's UID for permission validation
                    assignedUserName: assignedUserName,
                    isDone: false,
                    isEdited: false,
                    createdAt: Timestamp.fromDate(new Date()),
                    updatedAt: Timestamp.fromDate(new Date()),
                    createdBy: currentUser?.uid || "",
                    originalAssigneeId: newLead.assignedTo || "", // Store original assignee separately
                };

                followUpRef = await addDoc(collection(db, `companies/${userData?.companyId}/followUps`), newFollowUpData);
                await updateDoc(followUpRef, {
                    id: followUpRef.id,
                });
                hasActiveFollowUp = true;
                activeFollowUpId = followUpRef.id;
                activeFollowUpTime = newLead.followUpTime ? newLead.followUpTime : Timestamp.fromDate(new Date());
                activeFollowUpText = newLead.followUpText || null;
            } else {
                // Create new follow-up
                const assignedUserName = userData?.name || 'Unknown';
                
                const followUpData = {
                    followUp: newLead.followUpText,
                    followUpTime: newLead.followUpTime ? newLead.followUpTime : Timestamp.fromDate(new Date()),
                    leadId: editingId,
                    userId: newLead.assignedTo || "", // Must use current user's UID for permission validation
                    assignedUserName: assignedUserName,
                    isDone: false,
                    isEdited: false,
                    createdAt: Timestamp.fromDate(new Date()),
                    updatedAt: Timestamp.fromDate(new Date()),
                    createdBy: currentUser?.uid || "",
                    originalAssigneeId: newLead.assignedTo || "", // Store original assignee separately
                };

                followUpRef = await addDoc(collection(db, `companies/${userData?.companyId}/followUps`), followUpData);
                await updateDoc(followUpRef, {
                    id: followUpRef.id,
                });
                hasActiveFollowUp = true;
                activeFollowUpId = followUpRef.id;
                activeFollowUpTime = newLead.followUpTime ? newLead.followUpTime : Timestamp.fromDate(new Date());
                activeFollowUpText = newLead.followUpText || null;
            }
        } else {
            // Preserve existing follow-up status
            const existingLeadRef = doc(db, `companies/${userData?.companyId}/leads`, editingId);
            const existingLeadSnap = await getDoc(existingLeadRef);
            const existingLeadData = existingLeadSnap.data();

            hasActiveFollowUp = existingLeadData?.hasActiveFollowUp || false;
            activeFollowUpId = existingLeadData?.activeFollowUpId || null;
            activeFollowUpTime = existingLeadData?.activeFollowUpTime || null;
            activeFollowUpText = existingLeadData?.activeFollowUpText || null;
        }

        try {
            // Get existing lead data
            const existingLeadRef = doc(db, `companies/${userData?.companyId}/leads`, editingId);
            const existingLeadSnap = await getDoc(existingLeadRef);
            const existingLeadData = existingLeadSnap.data();

            // Clean alternate numbers - only save if they contain actual numbers beyond +91
            const cleanAlternateNumber1 = newLead.alternateNumber1?.trim();
            const cleanAlternateNumber2 = newLead.alternateNumber2?.trim();
            
            const alternateNumber1 = (cleanAlternateNumber1 && cleanAlternateNumber1 !== "+91" && cleanAlternateNumber1.length > 3) 
                ? cleanAlternateNumber1 : "";
            const alternateNumber2 = (cleanAlternateNumber2 && cleanAlternateNumber2 !== "+91" && cleanAlternateNumber2.length > 3) 
                ? cleanAlternateNumber2 : "";

            const updatedLead = await updateLead(db, currentUser, editingId, {
                name: (newLead.name ?? "").trim(),
                email: (newLead.email || "").trim(),
                number: newLead.number,
                alternateNumber1: alternateNumber1,
                alternateNumber2: alternateNumber2,
                reversedNumber: newLead.number ? newLead.number.split('').reverse().join('') : "",
                reversedAlternateNumber1: alternateNumber1 ? alternateNumber1.split('').reverse().join('') : "",
                reversedAlternateNumber2: alternateNumber2 ? alternateNumber2.split('').reverse().join('') : "",
                assignedTo: newLead.assignedTo || "",
                adminNotes: (newLead.adminNotes || ""),
                sourceId: newLead.sourceId || "",
                subSourceId: subSources.find(s => s.id === newLead.subSourceId && s.sourceId === newLead.sourceId)?.id || null,
                stageId: newLead.stageId || "",
                stateId: newLead.stateId || "",
                statusId: newLead.statusId || "",
                campaignId: newLead.campaignId || "",
                admissionStatusId: newLead.admissionStatusId || "",
                isActive: true,
                hasActiveFollowUp: hasActiveFollowUp,
                activeFollowUpId: activeFollowUpId,
                activeFollowUpTime: activeFollowUpTime,
                activeFollowUpText: activeFollowUpText,
                createdBy: currentUser ? currentUser.uid : "",
                updatedAt: Timestamp.fromDate(new Date()),
                createdAt: existingLeadData?.createdAt || Timestamp.fromDate(new Date()),
                destinationId: newLead.destinationId || "",
                pipelines: newLead.pipelines || [],
                intakeMonth: newLead.intakeMonth || "",
                intakeYear: newLead.intakeYear || 0,
                welcomeCallByAdmissionTeamStatusId: newLead.welcomeCallByAdmissionTeamStatusId || "",
                visaFileProcessingStatusId: newLead.visaFileProcessingStatusId || "",
                applicationMovedStatusId: newLead.applicationMovedStatusId || "",
                conditionalOfferLetterStatusId: newLead.conditionalOfferLetterStatusId || "",
                unconditionalOfferLetterStatusId: newLead.unconditionalOfferLetterStatusId || "",
                feePaymentStatusId: newLead.feePaymentStatusId || "",
                fileSubmissionStatusId: newLead.fileSubmissionStatusId || "",
                preferredStreamId: newLead.preferredStreamId || "",
                subAgentId: newLead.subAgentId || "",
                subAdmissionStatusId: newLead.subAdmissionStatusId || "",
                subStatusId: newLead.subStatusId || "",
            });

            if (onLeadUpdated) onLeadUpdated({ ...updatedLead, hasActiveFollowUp, activeFollowUpId, followUpText: newLead.followUpText || "" });
            if (onSuccess) onSuccess("Lead updated successfully");
            onClose();
            setNewLead({});
            window.location.reload();
        } catch (error) {
            console.error("Error updating lead:", error);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            if (onSuccess) onSuccess(`Error updating lead: ${errorMessage}`);
        } finally {
            setSubmitLoading(false);
        }
    };

    // Real-time phone number validation with debouncing
    useEffect(() => {
        if (!newLead.number || newLead.number.length < 1) {
            setPhoneValidationMessage("");
            setPhoneValidationType("");
            return;
        }

        const timeoutId = setTimeout(() => {
            validatePhoneRealTime(newLead.number || "");
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [newLead.number, editingId, userData?.companyId]);

    // Initialize lead data when modal opens or editing
    useEffect(() => {
        if (isOpen) {
            if (editingId && leadData) {
                setNewLead(leadData);

                // Initialize alternate phone state based on existing data
                const hasAlternateNumber1 = leadData.alternateNumber1 && leadData.alternateNumber1.trim() !== "";
                const hasAlternateNumber2 = leadData.alternateNumber2 && leadData.alternateNumber2.trim() !== "";

                if (hasAlternateNumber1 || hasAlternateNumber2) {
                    setIsAlternatePhoneRequired(true);
                    if (hasAlternateNumber2) {
                        setShowSecondAlternatePhone(true);
                    }
                } else {
                    setIsAlternatePhoneRequired(false);
                    setShowSecondAlternatePhone(false);
                }
            } else {
                // Initialize with empty lead data for new leads
                setNewLead({});
                // Reset alternate phone state for new leads
                setIsAlternatePhoneRequired(false);
                setShowSecondAlternatePhone(false);
            }
            // Clear phone validation state
            setPhoneValidationMessage('');
            setIsPhoneValidating(false);
            setPhoneValidationType('');
            // Clear update validation state
            setUpdateValidationMessage('');
            setUpdateValidationType('');
        }
    }, [isOpen, editingId, leadData, isStaff, currentScreen, currentUser]);

    if (!isOpen) {
        return null;
    }

    return (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-80 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl flex flex-col overflow-hidden">
                <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 1rem)' }}>
                    <div className="top-0 left-0 right-0 h-[40px] flex items-center justify-between rounded-tl-[12px] rounded-tr-[12px] p-4">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-full bg-gradient-to-b from-[#00A6D9] to-[#3483BE] flex items-center justify-center">
                                <img src={`${process.env.PUBLIC_URL}/images/desktop/ph_student.svg`} alt="Staff Icon" />
                            </div>
                            <h2 className="text-xl font-semibold text-black">{editingId ? "Edit Lead" : "Add Lead"}</h2>
                        </div>
                        <div className="text-right">
                            <button onClick={onClose} className="font-semibold text-3xl" style={{ background: 'linear-gradient(270deg, #A7C8E7 0%, #00A6D9 100%)', WebkitBackgroundClip: 'text', color: 'transparent', }}>
                                <img src={`${process.env.PUBLIC_URL}/images/desktop/close-btn.svg`} alt="Close" className="w-6 h-6" />
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-6 mt-8">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Name <span className="text-red-500">*</span></label>
                            <input
                                ref={inputRef}
                                type="text"
                                placeholder="Name"
                                value={newLead.name || ""}
                                onChange={handleChange}
                                className="border p-2 w-full mb-2"
                                style={{ borderRadius: "24px", border: "1px solid #3483BE" }}
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-6 mt-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Assigned To <span className="text-red-500">*</span></label>
                                <select
                                    value={newLead.assignedTo || ""}
                                    onChange={(e) => {
                                        const selectedUserId = e.target.value;
                                        setNewLead({ ...newLead, assignedTo: selectedUserId });
                                    }}
                                    className="border p-2 w-full mb-2"
                                    style={{
                                        borderRadius: '24px', border: '1px solid #3483BE',
                                    }}>
                                    <option value="">Assign To</option>
                                    {assignedToOptions.length > 0 ? (
                                        assignedToOptions.map((user) => (
                                            <option key={user.id} value={user.id}>
                                                {user.name}
                                            </option>
                                        ))
                                    ) : (
                                        <option disabled>No users available</option>
                                    )}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Phone <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Phone Number"
                                    value={newLead.number || ""}
                                    onFocus={() => handlePhoneFocus("number")}
                                    onBlur={() => handlePhoneBlur("number")}
                                    onChange={(e) =>
                                        handlePhoneChange("number", e.target.value)
                                    }
                                    className={`border p-2 w-full mb-2 ${phoneValidationType === 'error' ? 'border-red-500' :
                                        phoneValidationType === 'success' ? 'border-green-500' :
                                            phoneValidationType === 'warning' ? 'border-yellow-500' :
                                                'border-[#3483BE]'
                                        }`}
                                    style={{ borderRadius: "24px" }}
                                />
                                {(phoneValidationMessage || isPhoneValidating) && (
                                    <div className={`text-xs mt-1 ${phoneValidationType === 'error' ? 'text-red-500' :
                                        phoneValidationType === 'success' ? 'text-green-500' :
                                            phoneValidationType === 'warning' ? 'text-yellow-500' :
                                                'text-gray-500'
                                        }`}>
                                        {isPhoneValidating ? 'Validating...' : phoneValidationMessage}
                                    </div>
                                )}
                            </div>

                            {!isAlternatePhoneRequired ? (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Alternate Phone Number Required?
                                    </label>
                                    <div className="flex gap-2 mb-2">
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name="alternatePhoneRequired"
                                                value="yes"
                                                checked={false}
                                                onChange={() => setIsAlternatePhoneRequired(true)}
                                                className="mr-2"
                                            />
                                            Yes
                                        </label>
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name="alternatePhoneRequired"
                                                value="no"
                                                checked={true}
                                                onChange={() => {
                                                    setIsAlternatePhoneRequired(false);
                                                    setShowSecondAlternatePhone(false);
                                                    setNewLead({ ...newLead, alternateNumber1: "", alternateNumber2: "" });
                                                }}
                                                className="mr-2"
                                            />
                                            No
                                        </label>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Alternate Numbers
                                    </label>
                                    <div className="grid grid-cols-1 gap-4">
                                        <div>
                                            <input
                                                type="text"
                                                placeholder="Alternate Phone Number"
                                                value={newLead.alternateNumber1 || ""}
                                                onFocus={() => handlePhoneFocus("alternateNumber1")}
                                                onBlur={() => handlePhoneBlur("alternateNumber1")}
                                                onChange={(e) =>
                                                    handlePhoneChange("alternateNumber1", e.target.value)
                                                }
                                                className="border p-2 w-full"
                                                style={{ borderRadius: "24px", border: "1px solid #3483BE" }}
                                            />
                                        </div>

                                        <div>
                                            {!showSecondAlternatePhone ? (
                                                <button
                                                    type="button"
                                                    onClick={() => setShowSecondAlternatePhone(true)}
                                                    className="bg-blue-500 text-white px-3 py-2 rounded-full text-sm hover:bg-blue-600 h-full w-16"
                                                >
                                                    +
                                                </button>
                                            ) : (
                                                <input
                                                    type="text"
                                                    placeholder="Second Alternate Phone Number"
                                                    value={newLead.alternateNumber2 || ""}
                                                    onFocus={() => handlePhoneFocus("alternateNumber2")}
                                                    onBlur={() => handlePhoneBlur("alternateNumber2")}
                                                    onChange={(e) =>
                                                        handlePhoneChange("alternateNumber2", e.target.value)
                                                    }
                                                    className="border p-2 w-full"
                                                    style={{ borderRadius: "24px", border: "1px solid #3483BE" }}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-2 gap-6 mt-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Email</label>
                                <input
                                    type="email"
                                    placeholder="Email Address"
                                    value={newLead.email || ""}
                                    onChange={(e) =>
                                        setNewLead({ ...newLead, email: e.target.value })
                                    }
                                    className="border p-2 w-full mb-2"
                                    style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-6 mt-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Source <span className="text-red-500">*</span></label>
                            <select
                                value={newLead.sourceId || ""}
                                onChange={(e) => {
                                    const selectedSourceId = e.target.value;
                                    setNewLead({ ...newLead, sourceId: selectedSourceId });
                                }}
                                className={`border p-2 w-full mb-2 ${!isAdmin && !isStaff && !userData?.isManager ? 'bg-gray-200 appearance-none cursor-not-allowed font-semibold' : ''}`}
                                style={{
                                    borderRadius: '24px',
                                    border: '1px solid #3483BE',
                                }}
                                disabled={!isAdmin && !isStaff && !userData?.isManager}
                            >
                                <option value="">Select Source</option>
                                {sources.length > 0 ? (
                                    sources.map((source) => (
                                        <option key={source.id} value={source.id}>
                                            {source.source}
                                        </option>
                                    ))
                                ) : (
                                    <option disabled>No sources available</option>
                                )}
                            </select>
                        </div>

                        {newLead.sourceId && subSources.length > 0 && subSources.some(subSource => subSource.sourceId === newLead.sourceId) && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Sub Source</label>
                                <select
                                    value={newLead.subSourceId || ""}
                                    onChange={(e) => {
                                        const selectedSubSourceId = e.target.value;
                                        setNewLead({ ...newLead, subSourceId: selectedSubSourceId });
                                    }}
                                    className="border p-2 w-full mb-2"
                                    style={{
                                        borderRadius: '24px',
                                        border: '1px solid #3483BE',
                                    }}
                                >
                                    <option value="">Select Sub Source</option>
                                    {subSources.filter(subSource => subSource.sourceId === newLead.sourceId).map((subSource) => (
                                        <option key={subSource.id} value={subSource.id}>
                                            {subSource.subSource}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-medium text-gray-700">Campaign</label>
                            <select
                                value={newLead.campaignId || ""}
                                onChange={(e) => {
                                    const selectedCampaignId = e.target.value;
                                    setNewLead({ ...newLead, campaignId: selectedCampaignId });
                                }}
                                className="border p-2 w-full mb-2"
                                style={{
                                    borderRadius: '24px',
                                    border: '1px solid #3483BE',
                                }}
                            >
                                <option value="">Select Campaign</option>
                                {campaigns.length > 0 ? (
                                    campaigns.map((campaign) => (
                                        <option key={campaign.id} value={campaign.id}>
                                            {campaign.campaign}
                                        </option>
                                    ))
                                ) : (
                                    <option disabled>No campaign available</option>
                                )}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">Pipeline</label>
                            <select
                                onChange={handlePipelineSelection}
                                value=""
                                className="border p-2 w-full mb-2"
                                style={{
                                    borderRadius: '24px',
                                    border: '1px solid #3483BE',
                                }}
                            >
                                <option value="">Select Pipeline</option>
                                {pipelines.length > 0 ? (
                                    pipelines
                                        .filter(pipeline => currentUserPipelines?.includes(pipeline.id))
                                        .map((pipeline) => (
                                            <option key={pipeline.id} value={pipeline.id}>
                                                {pipeline.pipelineName}
                                            </option>
                                        ))
                                ) : (
                                    <option disabled>No Pipeline available</option>
                                )}
                            </select>

                            {(newLead.pipelines?.length ?? 0) > 0 && (
                                <div className="mt-2">
                                    <div className="flex flex-wrap gap-2">
                                        {newLead.pipelines?.map((pipelineId) => {
                                            const pipeline = pipelines.find(p => p.id === pipelineId);
                                            return (
                                                <div
                                                    key={pipelineId}
                                                    className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full text-sm text-gray-800"
                                                >
                                                    {pipeline ? pipeline.pipelineName : pipelineId}
                                                    {pipeline && currentUserPipelines.includes(pipeline.id) && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemovePipeline(pipelineId)}
                                                            className="text-red-500 cursor-pointer"
                                                        >
                                                            <img
                                                                src={`${process.env.PUBLIC_URL}/images/desktop/deleteicon.svg`}
                                                                alt="Remove"
                                                                className="w-4 h-4"
                                                            />
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Admin Notes */}
                    <div className="mt-8">
                        <label className="block text-sm font-medium text-gray-700">Admin Notes</label>
                        <div className="relative">
                            <textarea
                                placeholder="Admin Notes"
                                value={newLead.adminNotes || ""}
                                onChange={(e) =>
                                    setNewLead({ ...newLead, adminNotes: e.target.value })
                                }
                                className="border p-2 w-full mb-2"
                                style={{ borderRadius: '24px', border: '1px solid #3483BE', }}
                            />
                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex gap-4">
                                <img
                                    src={`${process.env.PUBLIC_URL}/images/desktop/deleteicon.svg`}
                                    alt="Delete Icon"
                                    className="w-5 h-5 cursor-pointer"
                                    onClick={() => setNewLead({ ...newLead, adminNotes: "" })}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Follow Up section */}
                    <div className="mt-8">
                        <label className="block text-sm font-medium text-gray-700">Follow Ups</label>
                        <div className="relative">
                            <textarea
                                placeholder="Add Follow Ups"
                                value={newLead.followUpText || ""}
                                onChange={(e) =>
                                    setNewLead({ ...newLead, followUpText: e.target.value })
                                }
                                className="border p-2 w-full mb-2"
                                style={{ borderRadius: '24px', border: '1px solid #3483BE', }}
                            />
                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex gap-4">
                                <img
                                    src={`${process.env.PUBLIC_URL}/images/desktop/deleteicon.svg`}
                                    alt="Delete Icon"
                                    className="w-5 h-5 cursor-pointer"
                                    onClick={() => setNewLead({ ...newLead, followUpText: "" })}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-6 mt-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Follow Up Time</label>
                            <input
                                type="datetime-local"
                                value={newLead.followUpTime ? convertToDateString(newLead.followUpTime) : ""}
                                placeholder="Follow Up Time"
                                min={new Date().toISOString().slice(0, 16)}
                                className="border p-2 w-full mb-2"
                                style={{
                                    borderRadius: '24px', border: '1px solid #3483BE',
                                }}
                                onChange={(e) => setNewLead({ ...newLead, followUpTime: Timestamp.fromDate(new Date(e.target.value)) })}
                            />
                        </div>
                    </div>

                    {/* Update Validation Message */}
                    {updateValidationMessage && (
                        <div className={`mt-4 p-3 rounded-lg text-sm ${
                            updateValidationType === 'error' ? 'bg-red-50 text-red-700 border border-red-200' :
                            updateValidationType === 'warning' ? 'bg-yellow-50 text-yellow-700 border border-yellow-200' :
                            updateValidationType === 'success' ? 'bg-green-50 text-green-700 border border-green-200' :
                            'bg-gray-50 text-gray-700 border border-gray-200'
                        }`}>
                            {updateValidationMessage}
                        </div>
                    )}

                    <GradientSubmitButton
                        loading={submitLoading}
                        onClick={editingId ? handleUpdateLead : handleAddLead}
                        label="Submit"
                        loadingLabel="Submitting..."
                    />
                </div>
            </div>
        </div>
    );
};

export default AddUpdateLeadModal;
